import type { ZodObject } from "zod";

// biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
export type SchemaWithDynamicUtils<T extends ZodObject<any>> = T & {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getTotalFields: (data: any) => number;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getInputCount: (data: any) => number;
  getRequiredTotalFields: () => number;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getRequiredInputCount: (data: any) => number;
  // 新しいメソッド：入れ子構造の個別フィールドをカウント
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getTotalNestedFields: (data: any) => number;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getInputNestedCount: (data: any) => number;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  getRequiredNestedInputCount: (data: any) => number;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
export function extendDynamicSchema<T extends ZodObject<any>>(
  schema: T,
  options: {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    ignoreCondition?: (data: any, fieldName: string) => boolean;
    requiredFields?: string[];
  } = {},
): SchemaWithDynamicUtils<T> {
  const keys = Object.keys(schema.shape);
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const shouldInclude = (data: any, key: string) =>
    !options.ignoreCondition || !options.ignoreCondition(data, key);
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const isFilled = (value: any) =>
    // dynamicSchemaを用いるフォームでは、boolean型はトグルスイッチで表されるため、falseを未入力とみなす
    value !== undefined && value !== null && value !== "" && value !== false;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  const countArrayField = (array: any[]): number =>
    array.reduce((count, item) => {
      if (typeof item === "object" && item !== null) {
        return (
          count +
          Object.keys(item).filter((k) => shouldInclude(item[k], k)).length
        );
      }
      return count + 1;
    }, 0);

  // 入れ子構造の個別フィールドを再帰的にカウントする関数
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
  const countNestedFields = (obj: any, countFilled = false): number => {
    const countRecursively = (
      // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
      currentValue: any,
      currentPath: string,
    ): number => {
      if (currentPath && !shouldInclude(obj, currentPath)) {
        return 0;
      }

      if (!isFilled(currentValue)) {
        return countFilled ? 0 : 1;
      }

      if (Array.isArray(currentValue)) {
        if (currentValue.length === 0) {
          return countFilled ? 0 : 1; // 空配列: 総フィールド数では1とカウントする
        }
        return currentValue.reduce((count: number, item, index) => {
          const itemPath = currentPath ? `${currentPath}.${index}` : `${index}`;
          return count + countRecursively(item, itemPath);
        }, 0);
      }

      if (typeof currentValue === "object") {
        return Object.entries(currentValue).reduce(
          (count: number, [key, value]) => {
            const fieldPath = currentPath ? `${currentPath}.${key}` : key;
            return count + countRecursively(value, fieldPath);
          },
          0,
        );
      }

      return 1;
    };

    return countRecursively(obj, "");
  };

  // パス形式のフィールド名から値を取得する関数
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> データ構造が動的なため
  const getValueByPath = (obj: any, path: string): any => {
    const parts = path.split(".");
    let current = obj;

    for (const part of parts) {
      if (
        current === null ||
        current === undefined ||
        typeof current !== "object"
      ) {
        return undefined;
      }
      current = current[part];
    }

    return current;
  };

  return Object.assign(schema, {
    // 元の実装（親オブジェクトレベルでのカウント）
    // 総フィールド数
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getTotalFields: (data: any) =>
      keys
        .filter((key) => shouldInclude(data, key))
        .reduce((count, key) => {
          const value = data[key];
          // 配列系のフィールド（e.g. storage, interface）は全要素の総フィールド数を計算
          return Array.isArray(value)
            ? count + countArrayField(value)
            : count + 1;
        }, 0),

    // 入力済みフィールド
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getInputCount: (data: any) =>
      keys
        .filter((key) => shouldInclude(data, key) && isFilled(data[key]))
        .reduce((count, key) => {
          const value = data[key];
          // 配列系のフィールド（e.g. storage, interface）は各要素に同様のフィルタを走らせた上で総フィールド数を計算
          return Array.isArray(value)
            ? count +
                value.reduce((sum, item) => {
                  if (typeof item === "object" && item !== null) {
                    return (
                      sum +
                      Object.keys(item).filter(
                        (k) => shouldInclude(data, k) && isFilled(item[k]),
                      ).length
                    );
                  }
                  return sum;
                }, 0)
            : count + 1;
        }, 0),

    // 必須項目の総数
    getRequiredTotalFields: () => (options.requiredFields || []).length,

    // 必須項目の入力済み数
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getRequiredInputCount: (data: any) =>
      (options.requiredFields || []).filter((key) => {
        const value = data[key];
        if (Array.isArray(value)) {
          return value.length > 0; // 配列に1つ以上要素があれば入力済みと見なす
        }
        return isFilled(value);
      }).length,

    // 新しい実装（入れ子構造の個別フィールドをカウント）
    // 総フィールド数（入れ子構造の個別フィールドをカウント）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getTotalNestedFields: (data: any) => countNestedFields(data, false),

    // 入力済みフィールド（入れ子構造の個別フィールドをカウント）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getInputNestedCount: (data: any) => countNestedFields(data, true),

    // 必須項目の入力済み数（パス形式の必須フィールドに対応）
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    getRequiredNestedInputCount: (data: any) =>
      (options.requiredFields || []).filter((fieldPath) => {
        const value = getValueByPath(data, fieldPath);
        if (Array.isArray(value)) {
          return value.length > 0; // 配列に1つ以上要素があれば入力済みと見なす
        }
        return isFilled(value);
      }).length,
  });
}
